import React, {useState, useEffect, useRef, useMemo} from "react";
import {Game} from "./Game";
import {useNavigate} from "react-router-dom";
import {GameSlide} from "./Slider";
import {getLangFromUrl} from "./Header";
import "./mobile-slider.css";

// visible = PEEK - GAP, so PEEK = desired_visible + GAP = 48 + 12 = 60
const PEEK = 48;
const GAP  = 12;

export const MobileSlider = ({data, loop = false}: {
    data: Game[];
    loop?: boolean;
    rtl?: boolean;
}) => {
    const lang = getLangFromUrl();
    const nav  = useNavigate();
    const count = data.length;

    // Measure container so slide widths stay in sync with layout
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const update = () => { if (el.offsetWidth > 0) setContainerWidth(el.offsetWidth); };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Each slide fills the container minus the peek space on both sides
    const slideWidth = containerWidth - 2 * PEEK;

    // Seamless loop: wrap with clones of the first and last slides
    const track = useMemo(
        () => loop && count > 1 ? [data[count - 1], ...data, data[0]] : data,
        [data, loop, count],
    );

    const [idx, setIdx] = useState(loop && count > 1 ? 1 : 0);
    // When false the CSS transition is suppressed for silent clone→real snaps
    const [animated, setAnimated] = useState(true);

    const next = () => { if (loop || idx < count - 1) setIdx(i => i + 1); };
    const prev = () => { if (loop || idx > 0)         setIdx(i => i - 1); };

    // After landing on a clone silently snap to the real counterpart
    const handleTransitionEnd = () => {
        if (!loop || count <= 1) return;
        if (idx <= 0)           { setAnimated(false); setIdx(count); }
        else if (idx >= count + 1) { setAnimated(false); setIdx(1); }
    };

    // Re-enable animation one frame after the silent snap is painted
    useEffect(() => {
        if (!animated) {
            const id = requestAnimationFrame(() =>
                requestAnimationFrame(() => setAnimated(true))
            );
            return () => cancelAnimationFrame(id);
        }
    }, [animated]);

    // Touch swipe
    const touchStartX = useRef<number | null>(null);
    const swiped = useRef(false);

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
        swiped.current = false;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 40) {
            swiped.current = true;
            delta > 0 ? next() : prev();
        }
        touchStartX.current = null;
    };

    // translateX so the active slide sits with PEEK space on both sides
    const translateX = -(idx * (slideWidth + GAP)) + PEEK;

    if (count === 0) return null;

    return (
        // Force ltr so translateX math is always left→right regardless of page direction
        <div ref={containerRef} className="ms" style={{direction: 'ltr'}}>
            {containerWidth > 0 && <div
                className="ms__track"
                style={{
                    transform: `translateX(${translateX}px)`,
                    transition: animated ? 'transform 350ms ease' : 'none',
                    gap: `${GAP}px`,
                }}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                onTransitionEnd={handleTransitionEnd}
            >
                {track.map((item, i) => (
                    <div
                        key={`${item.id}-${i}`}
                        className={`ms__slide${i === idx ? ' ms__slide--active' : ''}`}
                        style={{width: slideWidth}}
                        onClick={() => {
                            if (swiped.current) { swiped.current = false; return; }
                            if (i === idx) {
                                if (item.id) nav("/" + lang + "/" + item.id);
                            } else {
                                i > idx ? next() : prev();
                            }
                        }}
                    >
                        <GameSlide {...item}/>
                    </div>
                ))}
            </div>}
        </div>
    );
};
